import { i18n } from '../../../i18n/i18n'

import React, { Component, Fragment } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { produce } from 'immer'
import { debounce, isEqual, keys } from 'lodash'
import { RootState } from '../../../store'
import gs1Api, { Gs1Ai, Gs1Catalog, Gs1CheckResult, Gs1ElementResult, Gs1Issue } from '../../../apis/gs1.api'
import customLabelApi from '../../../apis/customLabel.api'
import Tooltip from '../../Tooltip/Tooltip'
import gs1BarcodeService, {
  gs1DateFormats,
  gs1EpcFilters,
  gs1ModuleDots,
  gs1SystemSources
} from '../../../services/gs1Barcode.service'
import {
  Gs1BarcodeElement,
  Gs1Config,
  Gs1Row,
  replaceGs1BarcodeConfig,
  replaceGs1BarcodeOpacity,
  replaceGs1BarcodePreview,
  replaceGs1BarcodeRotation
} from '../../../views/LabelBuilder/LabelBuilder.slice'
import Heading from '../../Heading'
import InputText from '../../InputText'
import Select, { SelectOption } from '../../Select'
import Checkbox from '../../Checkbox'
import PositionProperty from './PositionProperty'
import Gs1Glossary from './Gs1Glossary'

interface Props {
    selectedElement: Gs1BarcodeElement
}

type CombinedProps = Props & ConnectedProps<typeof connector>

interface State {
    catalog: Gs1Catalog | null
    result: Gs1CheckResult | null
    unavailable: boolean
    apiFields: string[]
    query: string
    glossaryOpen: boolean
}

/** Label data that is not an api: field, for labels other than custom labels. */
const inventoryFields = [
  'itemCode', 'itemDescription', 'caseCode', 'caseNumber', 'caseQuantity', 'palletCode', 'palletNumber',
  'workOrderCode', 'purchaseOrderCode', 'lineNumber', 'netWeight', 'grossWeight', 'customerPartNumber', 'vendorPartNumber'
]

/**
 * The GS1 Barcode panel. It replaces the free-text Content box with
 * structured rows: an Application Identifier, where its data comes from,
 * and a test value for the preview. All checking and drawing is done by
 * the API (/gs1/validate), so the panel and the printed label agree.
 */
class Gs1BarcodeProperties extends Component<CombinedProps, State> {
  state: State = {
    catalog: null,
    result: null,
    unavailable: false,
    apiFields: [],
    query: '',
    glossaryOpen: false
  }

  private requestSeq = 0

  componentDidMount () {
    gs1Api.catalog(catalog => this.setState({ catalog }))
    this.readApiFields()
    this.check()
  }

  componentDidUpdate (prevProps: CombinedProps) {
    const { selectedElement, dpi } = this.props
    if (prevProps.selectedElement.id !== selectedElement.id) {
      this.setState({ result: null, query: '' })
      this.check()
    } else if (!isEqual(prevProps.selectedElement.gs1, selectedElement.gs1) || prevProps.dpi !== dpi) {
      this.debouncedCheck()
    }
  }

  componentWillUnmount () {
    this.debouncedCheck.cancel()
  }

  // ─── API ──────────────────────────────────────────────────────────────

  check = () => {
    const { selectedElement, dpi } = this.props
    const id = selectedElement.id
    const seq = ++this.requestSeq
    gs1Api.check({ gs1: selectedElement.gs1, dpi }, result => {
      if (seq !== this.requestSeq || !result) return
      this.setState({ result, unavailable: false })
      this.props.replaceGs1BarcodePreview(id, result.preview, result.valid && !!result.preview)
    }, () => {
      if (seq !== this.requestSeq) return
      this.setState({ unavailable: true })
    })
  }

  debouncedCheck = debounce(this.check, 300)

  readApiFields () {
    const { customLabelId, target } = this.props
    if (target !== 'customLabel' || !customLabelId) {
      this.setState({ apiFields: inventoryFields })
      return
    }
    customLabelApi.read(customLabelId, customLabel => {
      if (!customLabel) return
      this.setState({ apiFields: keys(customLabel.attributes).filter(key => key.startsWith('api:')) })
    })
  }

  // ─── Editing ──────────────────────────────────────────────────────────

  get config (): Gs1Config {
    return this.props.selectedElement.gs1
  }

  get carrier () {
    return this.state.catalog?.carriers.find(carrier => carrier.id === this.config.carrier)
  }

  aiInfo (ai: string): Gs1Ai | undefined {
    return this.state.catalog?.ais.find(entry => entry.ai === ai)
  }

  update (recipe: (draft: Gs1Config) => void) {
    const next = produce(this.config, recipe)
    this.props.replaceGs1BarcodeConfig(this.props.selectedElement.id, next)
  }

  updateRow (index: number, recipe: (row: Gs1Row) => void) {
    this.update(draft => {
      recipe(draft.elements[index])
      draft.preset = 'custom'
    })
  }

  handleCarrierChange = (carrierId: string) => {
    const from = this.carrier
    const to = this.state.catalog?.carriers.find(carrier => carrier.id === carrierId)
    if (!to) return
    this.update(draft => {
      draft.carrier = carrierId
      if (to.mode === 'gtin') {
        // Carry the GTIN across; a GTIN-only barcode holds nothing else.
        const gtin = draft.elements.find(row => row.ai === '01')
        draft.elements = [gtin || gs1BarcodeService.presets('ai')[0].rows[0]]
        draft.preset = 'custom'
      } else if (!from || from.mode !== to.mode) {
        const preset = gs1BarcodeService.presets(to.mode)[0]
        if (from?.mode === 'gtin' || !draft.elements.length) {
          draft.elements = preset.rows.map(row => ({ ...row, source: { ...row.source } }))
          draft.preset = preset.key
        } else {
          draft.preset = 'custom'
        }
      }
    })
  }

  handlePresetClick = (key: string) => {
    const preset = this.carrier && gs1BarcodeService.presets(this.carrier.mode).find(p => p.key === key)
    if (!preset) return
    this.update(draft => {
      draft.preset = key
      draft.elements = preset.rows.map(row => ({ ...row, source: { ...row.source } }))
    })
  }

  handleAddAi = (ais: string[]) => {
    this.update(draft => {
      ais.forEach(ai => {
        if (draft.elements.some(row => row.ai === ai)) return
        draft.elements.push(gs1BarcodeService.newRow(ai, this.aiInfo(ai)?.title || ''))
      })
      draft.preset = 'custom'
    })
    this.setState({ query: '' })
  }

  handleRemoveRow = (index: number) => {
    this.update(draft => {
      draft.elements.splice(index, 1)
      draft.preset = 'custom'
    })
  }

  handleSourceChange = (index: number, choice: string) => {
    this.updateRow(index, row => {
      if (choice === 'fixed') {
        row.source = { kind: 'fixed', value: row.test_value || '' }
      } else if (choice.startsWith('system:')) {
        row.source = { kind: 'system', field: choice.slice(7), offset_days: 0 }
      } else if (choice === 'other') {
        row.source = { kind: 'api', field: 'api:' }
      } else {
        row.source = { kind: 'api', field: choice.slice(6) }
      }
    })
  }

  handleFix = (index: number, value: string) => {
    this.updateRow(index, row => {
      if (row.source.kind === 'fixed') row.source.value = value
      else row.test_value = value
    })
  }

  // ─── Rendering ────────────────────────────────────────────────────────

  sourceChoice (row: Gs1Row): string {
    if (row.source.kind === 'fixed') return 'fixed'
    if (row.source.kind === 'system') return `system:${row.source.field}`
    const field = row.source.field || ''
    return this.state.apiFields.includes(field) ? `field:${field}` : 'other'
  }

  renderCarrierSelect () {
    const { catalog } = this.state
    if (!catalog) return null
    const groups = Array.from(new Set(catalog.carriers.map(carrier => carrier.group)))
    return (
            <div className="block mb-2">
                <label className="block text-xs text-gray-700 mb-1">{i18n('Type')}</label>
                <select
                    className="form-select input-default appearance-none pr-8"
                    value={this.config.carrier}
                    onChange={event => this.handleCarrierChange(event.target.value)}>
                    {groups.map(group => (
                        <optgroup key={group} label={group}>
                            {catalog.carriers.filter(carrier => carrier.group === group).map(carrier => (
                                <option key={carrier.id} value={carrier.id}>{carrier.name}</option>
                            ))}
                        </optgroup>
                    ))}
                </select>
                {this.carrier && <p className="text-xs text-gray-500 mt-1">{this.carrier.capacity}. {this.carrier.use}</p>}
            </div>
    )
  }

  renderPresets () {
    if (!this.carrier || this.carrier.mode === 'gtin') return null
    const presets = gs1BarcodeService.presets(this.carrier.mode)
    const button = (key: string, name: string, onClick: () => void) => (
            <button
                key={key}
                type="button"
                onClick={onClick}
                className={`text-xs px-2 py-1 border rounded-full ${this.config.preset === key ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}>
                {name}
            </button>
    )
    return (
            <div className="mb-3">
                <label className="block text-xs text-gray-700 mb-1">Preset</label>
                <div className="flex flex-wrap gap-1.5">
                    {presets.map(preset => button(preset.key, preset.name, () => this.handlePresetClick(preset.key)))}
                    {button('custom', 'Custom', () => this.update(draft => { draft.preset = 'custom' }))}
                </div>
            </div>
    )
  }

  renderSourceControls (row: Gs1Row, index: number) {
    const info = this.aiInfo(row.ai)
    const choice = this.sourceChoice(row)
    const options: SelectOption[] = [
      ...this.state.apiFields.map(field => ({ name: field, value: `field:${field}` })),
      { name: 'Other data field…', value: 'other' },
      ...gs1SystemSources.map(source => ({ name: source.name, value: `system:${source.key}` })),
      { name: 'Fixed text', value: 'fixed' }
    ]

    return (
            <Fragment>
                <div className="grid grid-cols-2 gap-2">
                    <Select
                        name={`source-${index}`}
                        value={choice}
                        options={options}
                        label="Data source"
                        onChange={(_, value) => this.handleSourceChange(index, value)}/>
                    {row.source.kind === 'fixed'
                      ? <InputText
                            name={`fixed-${index}`}
                            value={row.source.value || ''}
                            label="Fixed value"
                            onChange={(_, value) => this.updateRow(index, r => { r.source.value = value })}/>
                      : row.source.kind === 'system'
                        ? (info?.isDate
                            ? <InputText
                                    type="number"
                                    name={`offset-${index}`}
                                    value={String(row.source.offset_days || 0)}
                                    label="Plus days"
                                    onChange={(_, value) => this.updateRow(index, r => { r.source.offset_days = Number(value) || 0 })}/>
                            : <div/>)
                        : <InputText
                                name={`test-${index}`}
                                value={row.test_value || ''}
                                label="Test value"
                                onChange={(_, value) => this.updateRow(index, r => { r.test_value = value })}/>}
                </div>
                {choice === 'other' && (
                    <InputText
                        name={`field-${index}`}
                        value={row.source.field || ''}
                        label="Data field name"
                        placeholder="api:field_name"
                        onChange={(_, value) => this.updateRow(index, r => { r.source.field = value })}/>
                )}
                {info?.isDate && row.source.kind === 'api' && (
                    <Select
                        name={`dateFormat-${index}`}
                        value={row.date_format || 'Y-m-d'}
                        options={gs1DateFormats}
                        label="Incoming date format"
                        onChange={(_, value) => this.updateRow(index, r => { r.date_format = value })}/>
                )}
            </Fragment>
    )
  }

  renderRow (row: Gs1Row, index: number) {
    const info = this.aiInfo(row.ai)
    const result: Gs1ElementResult | undefined = this.state.result?.elements[index]
    const resolved = this.state.result?.resolved[index]
    const isGtinOnly = this.carrier?.mode === 'gtin'
    const ok = result && !result.error

    return (
            <div key={`${row.ai}-${index}`} className={`border p-2 mb-2 ${result?.error ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-xs bg-blue-100 px-1.5">({row.ai})</span>
                    <span className="text-xs font-medium flex-1">{info?.title || 'Unknown AI'}</span>
                    <span className="font-mono text-xxs text-gray-500">{info?.format}{info?.fnc1 ? ' · FNC1' : ''}</span>
                    {result && <span className={`text-xxs font-medium ${ok ? 'text-green-700' : 'text-red-700'}`}>{ok ? 'Valid' : 'Error'}</span>}
                    {!isGtinOnly && (
                        <button type="button" className="text-gray-500 hover:text-red-600 text-sm" aria-label={`Remove (${row.ai})`} onClick={() => this.handleRemoveRow(index)}>✕</button>
                    )}
                </div>
                {this.renderSourceControls(row, index)}
                {result && (
                    <p className={`text-xs mt-1 ${ok ? 'text-green-700' : 'text-red-700'}`}>
                        {ok
                          ? [result.note, row.source.kind === 'system' && resolved ? `Printed today it would be ${resolved}.` : '']
                              .filter(Boolean).join(' · ')
                          : result.error}
                        {result.fix && row.source.kind !== 'system' && (
                            <button type="button" className="ml-2 underline" onClick={() => this.handleFix(index, result.fix!.value)}>{result.fix.label}</button>
                        )}
                    </p>
                )}
            </div>
    )
  }

  renderPicker () {
    const { catalog, result, query } = this.state
    if (!catalog || !result || this.carrier?.mode === 'gtin') {
      return this.carrier?.mode === 'gtin'
        ? <p className="text-xs text-gray-500 mb-3">{this.carrier.name} carries a {this.carrier.key} and nothing else. Choose GS1-128, GS1 DataBar Expanded or a 2D type to add lot, dates, serial or quantities.</p>
        : null
    }
    const q = query.trim().toLowerCase()
    const available = result.available
      .map(ai => this.aiInfo(ai))
      .filter((info): info is Gs1Ai => !!info && (!q || info.ai.startsWith(q) || info.title.toLowerCase().includes(q)))

    return (
            <div className="border border-dashed border-blue-300 p-2 mb-3">
                <InputText
                    name="gs1Query"
                    value={query}
                    label="Add data element"
                    placeholder="Search by number or name, e.g. 10, lot, weight"
                    onChange={(_, value) => this.setState({ query: value })}/>
                {q && (
                    <div className="max-h-48 overflow-y-auto">
                        {available.slice(0, 50).map(info => (
                            <button
                                key={info.ai}
                                type="button"
                                onClick={() => this.handleAddAi([info.ai])}
                                className="w-full flex gap-2 text-left text-xs px-1 py-1 hover:bg-blue-50">
                                <span className="font-mono">({info.ai})</span>
                                <span className="flex-1">{info.title}</span>
                                <span className="font-mono text-gray-500">{info.format}</span>
                            </button>
                        ))}
                        {!available.length && <p className="text-xs text-gray-500 p-1">Nothing matches.</p>}
                    </div>
                )}
                <p className="text-xxs text-gray-500 mt-1">
                    {result.available.length} of {catalog.ais.length} Application Identifiers can be added. Hidden: ones already used, ones that conflict with an element on the barcode, and ones that no longer fit.
                </p>
            </div>
    )
  }

  renderCompliance () {
    const { result, unavailable } = this.state
    if (unavailable) {
      return <p className="text-xs text-red-700 mb-3">The GS1 checks could not be reached. Changes are kept; the barcode will be checked again on the next edit.</p>
    }
    if (!result) return <p className="text-xs text-gray-500 mb-3">Checking…</p>

    // Template problems (RFID settings, missing data fields...) block
    // activation too, so they count as errors here. Ones the value checks
    // already reported are not repeated.
    const valueErrors = result.issues.filter(issue => issue.level === 'error')
    const templateErrors = (result.templateIssues || [])
      .filter(message => !valueErrors.some(issue => issue.message === message))
      .map((message): Gs1Issue => ({ level: 'error', code: 'template', message }))
    // An EPC that cannot be built is a warning, not an error: the barcode
    // prints, only the tag goes unwritten. It still belongs in this summary
    // rather than only in the RFID section further down, or the panel reads
    // a clean "GS1 compliant" for a label that will come off the printer
    // without the tag someone is expecting. Raised only once nothing else is
    // wrong - while the data or the RFID settings are themselves at fault,
    // those errors say the same thing already.
    const errors = [...valueErrors, ...templateErrors]
    const epcWarnings: Gs1Issue[] = this.config.rfid?.enabled && result.valid &&
      !errors.length && result.epc?.error
      ? [{ level: 'warning', code: 'rfid', message: `RFID tag will not be written. ${result.epc.error}` }]
      : []
    const warnings = [...result.issues.filter(issue => issue.level === 'warning'), ...epcWarnings]
    const mmPerModule = (this.config.module_dots * 25.4 / this.props.dpi).toFixed(3)
    const countWarnings = warnings.length ? `${warnings.length} warning${warnings.length > 1 ? 's' : ''}` : ''
    const summary = () => {
      if (!errors.length) {
        // Compliant either way - a warning here means the barcode prints and
        // something alongside it does not, so say both rather than hiding
        // one behind the other.
        return countWarnings ? `GS1 compliant: ${countWarnings}` : 'GS1 compliant'
      }
      const counted = `${errors.length} error${errors.length > 1 ? 's' : ''}${countWarnings ? `, ${countWarnings}` : ''}`
      return valueErrors.length ? `Not GS1 compliant: ${counted}` : `Not ready to activate: ${counted}`
    }

    return (
            <Fragment>
                <label className="block text-xs text-gray-700 mb-1">
                    Encoded data
                    {this.help('Exactly what the barcode carries. [FNC1] marks a separator the symbol inserts for you; the bracketed numbers are Application Identifiers naming each value.')}
                </label>
                <pre className="text-xs bg-gray-100 p-2 mb-1 whitespace-pre-wrap break-all">{result.encoded || 'Available once every value is valid.'}</pre>
                <p className="text-xxs text-gray-500 mb-2">Order, FNC1 separators and parentheses in the readable text are handled for you.</p>
                <div className={`border p-2 mb-3 text-xs ${errors.length ? 'border-red-400' : warnings.length ? 'border-yellow-500' : 'border-green-600'}`}>
                    <p className={`font-medium mb-1 ${errors.length ? 'text-red-700' : warnings.length ? 'text-yellow-700' : 'text-green-700'}`}>
                        {summary()}
                    </p>
                    {errors.map((issue, i) => (
                        <p key={`e${i}`} className="text-red-700">
                            {issue.message}
                            {issue.add && issue.add.length > 0 && (
                                <button type="button" className="ml-2 underline" onClick={() => this.handleAddAi(issue.add!)}>
                                    Add {issue.add.map(ai => `(${ai})`).join(' and ')}
                                </button>
                            )}
                        </p>
                    ))}
                    {warnings.map((issue, i) => <p key={`w${i}`} className="text-yellow-700">{issue.message}</p>)}
                    {!errors.length && (
                        <Fragment>
                            <p>Structure and required pairs: OK</p>
                            <p>X-dimension: {mmPerModule} mm ({this.config.module_dots} dots at {this.props.dpi} dpi)</p>
                        </Fragment>
                    )}
                    {errors.length > 0 && <p className="text-gray-600 mt-1">This label cannot be activated for printing until the errors are resolved.</p>}
                    {epcWarnings.length > 0 && <p className="text-gray-600 mt-1">The barcode itself is fine and will print. Only the tag is affected - correct the company prefix below to write it, or turn RFID off if this label does not need one.</p>}
                </div>
                {this.renderTroubleshooting()}
            </Fragment>
    )
  }

  /**
   * A hover explanation beside a field or a term.
   *
   * Rendered here rather than through InputText's label, which takes a plain
   * string: every form in the product shares that component and none of them
   * needs this.
   */
  help (text: string) {
    return (
            <Tooltip text={text} maxWidth={320}>
                <span
                    className="ml-1 text-gray-400 cursor-help select-none text-xxs align-middle"
                    aria-label={text}
                    role="img">&#9432;</span>
            </Tooltip>
    )
  }

  /**
   * The three mistakes that actually cost people time here, written out.
   *
   * Each one is a real support round-trip: a check digit typed from a
   * printed label that was already wrong, a company prefix missing its
   * leading zero, and a filter value nobody could place. Having them on the
   * panel means a user can resolve their own barcode instead of asking what
   * the red text means.
   */
  renderTroubleshooting () {
    const notes: Array<[string, string]> = [
      ['The check digit is rejected',
        'The last digit of a GTIN or SSCC is calculated from the ones before it, so a single mistyped digit anywhere makes it wrong. Use the fix button on the row to let us work it out rather than typing it from a printed label.'],
      ['The company prefix "is not in the GTIN"',
        'The prefix is always the key\u2019s leading digits after the first one; your GS1 licence tells you how many. Dropping or adding its leading zero is the usual cause - 0840080 and 8400803 look alike but only one of them is inside GTIN 10840080311347.'],
      ['Which filter value to choose',
        'It tells RFID readers what kind of thing carries the tag so they can ignore the rest: 1 for a single item, 2 for a full case, 6 for a pallet or other unit load.']
    ]

    return (
            <details className="border border-gray-200 mb-3">
                <summary className="text-xs text-gray-700 px-2 py-1.5 cursor-pointer select-none">
                    Common problems
                </summary>
                <div className="px-2 pb-2">
                    {notes.map(([title, body]) => (
                        <p key={title} className="text-xxs text-gray-600 mt-1.5">
                            <span className="font-medium text-gray-800">{title}. </span>{body}
                        </p>
                    ))}
                </div>
            </details>
    )
  }

  /**
   * RFID: the tag's EPC is built from this barcode's data at print time.
   * SGTIN needs (01) with (21); SSCC needs (00). The label must print as ZPL.
   */
  renderRfid () {
    const rfid = this.config.rfid || { enabled: false, company_prefix: '', filter: 1 }
    const epc = this.state.result?.epc
    const updateRfid = (recipe: (draft: NonNullable<Gs1Config['rfid']>) => void) => {
      this.update(draft => {
        draft.rfid = draft.rfid || { enabled: false, company_prefix: '', filter: 1 }
        recipe(draft.rfid)
      })
    }

    return (
            <div className="border border-gray-200 p-2 mb-3">
                <Checkbox
                    name="rfid"
                    align="start"
                    label="Encode an RFID tag (EPC) from this barcode's data"
                    checked={rfid.enabled}
                    onChange={() => updateRfid(draft => { draft.enabled = !draft.enabled })}/>
                {!rfid.enabled && (
                    <p className="text-xxs text-gray-500 mt-1">SGTIN from (01) GTIN with (21) serial, or SSCC from (00). Written by RFID printers through ZPL.</p>
                )}
                {rfid.enabled && (
                    <Fragment>
                        {this.props.hasRfidElement && (
                            <p className="text-xs text-red-700 mb-2">This label also has an RFID element. A label can write its tag from one place only: remove the RFID element or turn this off.</p>
                        )}
                        <div className="grid grid-cols-2 gap-2">
                            <InputText
                                name="companyPrefix"
                                value={rfid.company_prefix}
                                label="GS1 Company Prefix"
                                placeholder="e.g. 0614141"
                                onChange={(_, value) => updateRfid(draft => { draft.company_prefix = value.replace(/\D/g, '') })}/>
                            <Select
                                name="filter"
                                value={String(rfid.filter)}
                                options={gs1EpcFilters}
                                label="Filter value"
                                onChange={(_, value) => updateRfid(draft => { draft.filter = Number(value) })}/>
                        </div>
                        <p className="text-xxs text-gray-500 mb-1">
                            The company prefix comes from your GS1 licence. It is always the GTIN's leading digits after the indicator digit - what the licence tells you is how many of them it covers.
                            {this.help('Watch the leading zero: many prefixes begin with one, and dropping it shifts every digit along so the prefix no longer appears in the key at all.')}
                        </p>
                        <label className="block text-xs text-gray-700 mb-1">
                            RFID tag (EPC)
                            {this.help('The identifier written into the tag, derived from this barcode\u2019s own data. SGTIN comes from (01) GTIN with (21) serial; SSCC comes from (00).')}
                        </label>
                        {epc && epc.hex
                          ? (
                            <pre className="text-xs bg-gray-100 p-2 whitespace-pre-wrap break-all">{`${epc.scheme} · ${epc.hex}\n${epc.uri}`}</pre>
                            )
                          : <p className="text-xs text-red-700">{epc?.error || 'Available once the barcode is valid.'}</p>}
                        <p className="text-xxs text-gray-500 mt-1">
                            {this.state.result?.valid && epc?.error
                              ? 'This is not only about the test values: the prefix has to sit inside the key of every print, so live data fails the same way until it matches. RFID labels print through ZPL only.'
                              : 'Built from the test values here; each print uses its own data. RFID labels print through ZPL only.'}
                        </p>
                    </Fragment>
                )}
            </div>
    )
  }

  renderSettings () {
    const linear = this.carrier && !/datamatrix|qrcode|dotcode|dlqr|dldatamatrix/.test(this.carrier.id)
    const { selectedElement } = this.props
    return (
            <Fragment>
                {this.carrier?.mode === 'dl' && (
                    <InputText
                        name="domain"
                        value={this.config.digital_link_domain || ''}
                        label="Web address for the Digital Link"
                        placeholder="https://id.example.com"
                        onChange={(_, value) => this.update(draft => { draft.digital_link_domain = value })}/>
                )}
                <div className="grid grid-cols-2 gap-2">
                    <Select
                        name="moduleDots"
                        value={String(this.config.module_dots)}
                        options={gs1ModuleDots.map(dots => ({ name: `${dots} dots (${(dots * 25.4 / this.props.dpi).toFixed(2)} mm)`, value: String(dots) }))}
                        label="Module width"
                        onChange={(_, value) => this.update(draft => { draft.module_dots = Number(value) })}/>
                    {linear
                      ? <InputText
                            type="number"
                            name="barHeight"
                            value={String(this.config.bar_height_mm)}
                            label="Bar height (mm)"
                            onChange={(_, value) => this.update(draft => { draft.bar_height_mm = Math.max(2, Number(value) || 2) })}/>
                      : <div/>}
                    <Select
                        name="rotation"
                        value={String(selectedElement.rotation || 0)}
                        options={['0', '90', '180', '270'].map(r => ({ name: `${r}°`, value: r }))}
                        label="Rotation"
                        onChange={(_, value) => this.props.replaceGs1BarcodeRotation(selectedElement.id, Number(value))}/>
                    <InputText
                        type="number"
                        name="opacity"
                        value={String(selectedElement.opacity)}
                        label={i18n('Transparency')}
                        enforceLimits={true}
                        onChange={(_, value) => this.props.replaceGs1BarcodeOpacity(selectedElement.id, Number(value))}/>
                </div>
                <Checkbox
                    name="humanReadable"
                    align="start"
                    label="Show human-readable text"
                    checked={this.config.human_readable}
                    onChange={() => this.update(draft => { draft.human_readable = !draft.human_readable })}/>
                <Select
                    name="onInvalid"
                    value={this.config.on_invalid}
                    options={[
                      { name: 'Reject the print job and return errors per element', value: 'reject' },
                      { name: 'Print the label without this barcode and return a warning', value: 'warn' }
                    ]}
                    label="If print data is invalid"
                    onChange={(_, value) => this.update(draft => { draft.on_invalid = value as Gs1Config['on_invalid'] })}/>
            </Fragment>
    )
  }

  render () {
    const { selectedElement } = this.props
    const { catalog } = this.state
    if (!selectedElement || selectedElement.type !== 'gs1_barcode') return null

    return (
            <Fragment>
                <div className="flex items-center justify-between">
                    <Heading className="mb-1" text="GS1 Barcode" size="small"/>
                    {catalog && (
                        <button type="button" className="text-xs underline text-blue-700" onClick={() => this.setState({ glossaryOpen: true })}>
                            Glossary
                        </button>
                    )}
                </div>
                <PositionProperty elementId={selectedElement.id} x={selectedElement.x} y={selectedElement.y}/>
                {this.renderCarrierSelect()}
                {this.renderPresets()}
                <div className="flex items-center justify-between mb-1">
                    <label className="text-xs text-gray-700">Data elements</label>
                    <span className="text-xxs text-gray-500">Test values drive the preview only</span>
                </div>
                {this.config.elements.map((row, index) => this.renderRow(row, index))}
                {this.renderPicker()}
                {this.renderCompliance()}
                {this.renderRfid()}
                {this.renderSettings()}
                {catalog && (
                    <Gs1Glossary catalog={catalog} isOpen={this.state.glossaryOpen} onClose={() => this.setState({ glossaryOpen: false })}/>
                )}
            </Fragment>
    )
  }
}

function mapStateToProps (state: RootState) {
  const { markup } = state.labelBuilder
  return {
    hasRfidElement: markup.elements.some(element => element.type === 'rfid'),
    dpi: markup.stage.dpi || 203,
    customLabelId: markup.customLabelId,
    target: markup.target
  }
}

const mapDispatchToProps = {
  replaceGs1BarcodeConfig,
  replaceGs1BarcodePreview,
  replaceGs1BarcodeRotation,
  replaceGs1BarcodeOpacity
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(Gs1BarcodeProperties)
